// "Get park money" on the main page as well as in the shop, so nobody misses that the packs exist.
// The packs, their prices and whether they are on come from our server (the park office keeps each
// store's list); the payment is Lagos Life's own sheet (or, for our own website's players, a payment
// company's page through Stig Labs pay), and our server checks every purchase before a coin lands
// (lib/coins). A pack shows its park money (coins in the engine, 1 coin = ₦100 on screen, written short) with the words "park money", and the price on the button in plain digits, so the
// two never read as one another. Park money buys looks only.
import { isFramed } from '@wahala/vatar-client';
import { useEffect, useState } from 'react';
import { api, type ShopState } from '../lib/api';
import { buyPack, checkoutPack } from '../lib/coins';
import { parkNaira } from '../lib/money';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { Html, W } from './wahala';
import './get-coins.css';

/**
 * note: also say what coins are and are not (the shop's line). onBought: a pack went through (the
 * shop reloads its own balance).
 */
export function GetCoins({ note = false, onBought }: { note?: boolean; onBought?: () => void } = {}) {
  const set = useUi((s) => s.set);
  const [packs, setPacks] = useState<Pick<ShopState, 'packs' | 'packsOn' | 'checkout'> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let gone = false;
    api
      .shop()
      .then((s) => {
        if (gone) return;
        setPacks({ packs: s.packs ?? [], packsOn: s.packsOn === true, checkout: s.checkout ?? 'lagoslife' });
        if (typeof s.coins === 'number') set({ coins: s.coins });
      })
      .catch(() => {});
    return () => {
      gone = true;
    };
  }, [set]);

  if (!packs || packs.packs.length === 0) return null;
  // Lagos Life's sheet opens only inside its frame; our website's payment page only outside any frame
  const website = packs.checkout === 'website';
  const on = packs.packsOn && (website ? !isFramed() : isFramed());

  return (
    <section className="card get-coins" aria-labelledby="get-coins-title">
      <div className="get-coins__head">
        <span id="get-coins-title" className="label">
          {S.shop.getCoins}
        </span>
        <span className="muted small">
          {on ? (website ? S.shop.getCoinsPitchWebsite : S.shop.getCoinsPitch) : S.shop.packsOff}
        </span>
      </div>
      <ul className="get-coins__list">
        {packs.packs.map((p) => (
          <li key={p.productId}>
            <button
              type="button"
              className="get-coins__pack"
              disabled={!on || busy}
              aria-label={S.shop.packBuyFor(parkNaira(p.coins), p.price)}
              onClick={async () => {
                setBusy(true);
                try {
                  // the payment page takes the phone away; it comes back to the play host (lib/coins)
                  if (website) await checkoutPack(p.productId);
                  else {
                    await buyPack(p.productId);
                    onBought?.();
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Html
                className="get-coins__amount"
                html={`${W().coinGlyph({ size: 16 })}<span>${parkNaira(p.coins)}</span>`}
              />
              {/* the word under the amount: it is park money, never the price Lagos Life charges */}
              <span className="get-coins__word">{S.shop.packWord}</span>
              <span className="get-coins__buy">{S.shop.packPrice(p.price)}</span>
            </button>
          </li>
        ))}
      </ul>
      {note && on && <p className="muted small">{website ? S.shop.packNoteWebsite : S.shop.packNote}</p>}
    </section>
  );
}
